import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../helpers/constants";
import { getValueOrBlank, isEmpty } from "../../../helpers/utils";
import { CPParticipantsResultRowValue } from "../../../pages/coachPortal/modelsAndSchemas/Models";
import {
  AddGoalParams,
  GoalDetailResponse,
  GoalSelectionResponse,
  UpdateGoalParams,
} from "../../../pages/createProgram/components/createProgramTabs/tabs/participant/components/participantGoalSelection/modelsAndSchemas/Models";
import {
  ParticipantActivityResponse,
  ParticipantActivityTimelineParams,
  ParticipantDetail,
  ProgramDetail,
  ProgramStatusList,
} from "../../../pages/createProgram/modelsAndSchemas/Models";
import apiInstance from "../../instances/apiInstance";
import {
  GetProgramFeeResponse,
  IdNameList,
  SaveProgramFeeParams,
} from "../common/models";
import {
  AddExistingProgramsParams,
  AddFeeOrExpenseRequestPayload,
  CoachFunction,
  CoachMetricsApiResponse,
  CoachMetricsParams,
  CreateProgramParams,
  currencyExchange,
  FunctionCoach,
  GetBillingDetailsPayload,
  GetBillingDetailsResponse,
  GetRevenueSplitPayload,
  ManageParticipantsMetricsCountResponse,
  MigratedRevenueSplitResponse,
  MyMeetingsResponse,
  ParticipantActivityParams,
  ParticipantsListApiResponse,
  ParticipantsListPayload,
  ProgramDocumentDetailsResponse,
  ProgramDocumentUploadParams,
  ProgramExpenseData,
  ProgramTransferParams,
  RevenueSplitResponse,
  SaveParticipantParams,
  SaveProgramParams,
  SearchCoachPortalParticipantsParams,
  SearchGenericParticipantsParams,
  SearchNISParticipantsParams,
  SearchProgramIndividualParams,
  SearchProgramParams,
  SecondaryClosingParams,
  SessionsAndEventsResponse,
  StatusParams,
  TaxType,
  TimeToLandParams,
  UpdateProgramParams,
  UpdateProgramStatusParams,
  UpdateRevenueSplitPayload,
  ViewFeeOrExpenseDetails,
} from "./models";

export const useCreateProgramMutation = () => {
  return useMutation({
    mutationFn: async (params: CreateProgramParams) => {
      const response = await apiInstance.post(
        API_ENDPOINTS.CREATE_PROGRAM,
        params,
      );
      return response.data;
    },
  });
};

export const useProgramTransfer = () => {
  return useMutation({
    mutationFn: async ({ params }: { params: ProgramTransferParams }) => {
      const response = await apiInstance.post(
        API_ENDPOINTS.getProgramTransfer(params?.programId ?? ""),
        params,
      );
      return response;
    },
    retry: false,
  });
};

export const useDeleteTransferActivity = () => {
  return useMutation({
    mutationFn: (programId: string) => {
      return apiInstance.delete(
        `${API_ENDPOINTS.deleteTransferActivity(programId ?? "")}`,
      );
    },
  });
};

export const useSearchProgramMutation = () => {
  return useMutation({
    mutationFn: (
      params: SearchProgramParams | SearchProgramIndividualParams,
    ) => {
      return apiInstance.post(API_ENDPOINTS.SEARCH_PROGRAM, params);
    },
    retry: false,
  });
};

export const useSearchNISParticipantsMutation = () => {
  return useMutation({
    mutationFn: (params: SearchNISParticipantsParams) => {
      return apiInstance.post(API_ENDPOINTS.SEARCH_NIS_PARTICIPANTS, params);
    },
    retry: false,
  });
};

export const useSearchGenericAccountParticipantsMutation = () => {
  return useMutation({
    mutationFn: (params: SearchGenericParticipantsParams) => {
      return apiInstance.post(
        API_ENDPOINTS.SEARCH_GENERIC_ACCOUNT_PARTICIPANTS,
        params,
      );
    },
    retry: false,
  });
};

export const useEligibleSearchProgramMutation = () => {
  return useMutation({
    mutationFn: (params: AddExistingProgramsParams) => {
      return apiInstance.post(API_ENDPOINTS.SEARCH_ELIGIBLE_PROGRAM, params);
    },
    retry: false,
  });
};

export const useGetMyMeetingsQuery = () => {
  return useQuery({
    queryKey: ["getMyMeetings"],
    queryFn: async (): Promise<MyMeetingsResponse[]> => {
      const response = await apiInstance.get(API_ENDPOINTS.GET_MY_MEETINGS);
      return response.data as MyMeetingsResponse[];
    },
    retry: false,
  });
};

export const useGetMyCaseLoadsParticipantsDetails = () => {
  const [params, setParams] = useState<{
    loggedInCoachId: string;
    ageingKey: string;
  }>();

  const query = useQuery({
    queryKey: ["participants", params?.ageingKey, params?.loggedInCoachId],
    queryFn: async (): Promise<CPParticipantsResultRowValue[]> => {
      const res = await apiInstance.post(API_ENDPOINTS.COACH_PORTAL, {
        aging: params?.ageingKey,
        coachIds: [params?.loggedInCoachId],
      });
      return res.data;
    },
    enabled: !isEmpty(params?.loggedInCoachId),
    retry: false,
  });

  const refetchWithParams = (loggedInCoachId: string, ageingKey: string) => {
    setParams({ loggedInCoachId, ageingKey });
  };

  return { ...query, refetchWithParams };
};

const fetchParticipantsWithPayload = async (
  coachId: string,
  payload?: Record<string, unknown>,
): Promise<CPParticipantsResultRowValue[]> => {
  if (isEmpty(coachId)) return [];

  const res = await apiInstance.post(API_ENDPOINTS.COACH_PORTAL, {
    ...payload,
    coachIds: [coachId],
  });
  return res.data;
};

export const useParticipantLandedDetails = ({
  loggedInCoachId,
}: {
  loggedInCoachId: string;
}) => {
  return useQuery({
    queryKey: ["useParticipantLandedDetails", loggedInCoachId],
    queryFn: async (): Promise<CPParticipantsResultRowValue[]> => {
      const res = await fetchParticipantsWithPayload(loggedInCoachId, {
        hasSuccessfullyLanded: true,
      });
      return res;
    },
    retry: false,
  });
};

const PARTICIPANT_FILTERS = {
  assigned: {},
  notRegistered: { notRegistered: true },
  notLoggedInLast30Days: { notLoggedInLast30Days: true },
  noCoachingReceivedInLast30Days: { noCoachingReceivedInLast30Days: true },
} as const;

export const useGetMyCaseLoadsVsEngagementParticipantsDetails = ({
  loggedInCoachId,
}: {
  loggedInCoachId: string;
}) => {
  // First query: assigned
  const assignedQuery = useQuery({
    queryKey: ["participants", "assigned", loggedInCoachId],
    queryFn: async (): Promise<CPParticipantsResultRowValue[]> => {
      const res = await fetchParticipantsWithPayload(
        loggedInCoachId,
        PARTICIPANT_FILTERS.assigned,
      );
      return res;
    },
    enabled: !isEmpty(loggedInCoachId),
    retry: false,
  });

  // Second query: notRegistered (runs only after first query succeeds)
  const notRegisteredQuery = useQuery({
    queryKey: ["participants", "notRegistered", loggedInCoachId],
    queryFn: async (): Promise<CPParticipantsResultRowValue[]> => {
      const res = await fetchParticipantsWithPayload(
        loggedInCoachId,
        PARTICIPANT_FILTERS.notRegistered,
      );
      return res;
    },
    enabled: !isEmpty(loggedInCoachId) && assignedQuery.isFetchedAfterMount,
    retry: false,
  });

  // Third query: notLoggedInLast30Days (runs only after second query succeeds)
  const notLoggedInLast30DaysQuery = useQuery({
    queryKey: ["participants", "notLoggedInLast30Days", loggedInCoachId],
    queryFn: async (): Promise<CPParticipantsResultRowValue[]> => {
      const res = await fetchParticipantsWithPayload(
        loggedInCoachId,
        PARTICIPANT_FILTERS.notLoggedInLast30Days,
      );
      return res;
    },
    enabled:
      !isEmpty(loggedInCoachId) && notRegisteredQuery.isFetchedAfterMount,
    retry: false,
  });

  // Fourth query: noCoachingReceivedInLast30Days (runs only after third query succeeds)
  const noCoachingReceivedInLast30DaysQuery = useQuery({
    queryKey: [
      "participants",
      "noCoachingReceivedInLast30Days",
      loggedInCoachId,
    ],
    queryFn: async (): Promise<CPParticipantsResultRowValue[]> => {
      const res = await fetchParticipantsWithPayload(
        loggedInCoachId,
        PARTICIPANT_FILTERS.noCoachingReceivedInLast30Days,
      );
      return res;
    },
    enabled:
      !isEmpty(loggedInCoachId) &&
      notLoggedInLast30DaysQuery.isFetchedAfterMount,
    retry: false,
  });

  // Return in the same format as useQueries for backward compatibility
  return [
    assignedQuery,
    notRegisteredQuery,
    notLoggedInLast30DaysQuery,
    noCoachingReceivedInLast30DaysQuery,
  ];
};

//programDetail Api
export const useGetProgramDetailById = () => {
  const [params, setParams] = useState<string>();

  const query = useQuery({
    queryKey: ["getProgramDetailById", params],
    queryFn: async (): Promise<ProgramDetail> => {
      if (!params) {
        return {} as ProgramDetail;
      }
      const res = await apiInstance.get(
        API_ENDPOINTS.getProgramDetailById(params),
      );
      return res.data;
    },
    enabled: !!params, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (newParams: string) => {
    setParams(newParams);
    query.refetch();
  };

  return { ...query, refetchWithParams };
};

export const useGetCompatibleProgramTemplates = () => {
  const [params, setParams] = useState<string>();
  const query = useQuery({
    queryKey: ["useGetCompatibleProgramTemplates", params],
    queryFn: async () => {
      if (!params) {
        return [];
      }
      const res = await apiInstance.get(
        API_ENDPOINTS.getCompatibleProgramTemplates(params),
      );
      return res.data;
    },
    enabled: !!params, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (newParams: string) => {
    setParams(newParams);
    query.refetch();
  };

  return { ...query, refetchWithParams };
};

export const useGetDestinationPrograms = () => {
  const [params, setParams] = useState<{
    programId?: string;
    queryParam?: string; // Can be countryCode or programTemplateId
    isCountry?: boolean; // Flag to track if it's countryCode
  }>({});

  const query = useQuery({
    queryKey: ["useGetDestinationPrograms", params],
    queryFn: async () => {
      if (!params.programId || !params.queryParam) {
        return [];
      }
      const res = await apiInstance.get(
        API_ENDPOINTS.getDestinationPrograms(
          params.programId ?? "",
          params.queryParam ?? "",
          params.isCountry ?? false,
        ),
      );
      return res.data;
    },
    enabled: !!params.programId && !!params.queryParam,
    retry: false,
  });

  const refetchWithParams = (
    programId: string,
    queryParam: string,
    isCountry: boolean,
  ) => {
    setParams({ programId, queryParam, isCountry });
    query.refetch();
  };

  return { ...query, refetchWithParams };
};

export const useGetProgramStatusListId = () => {
  const [params, setParams] = useState<StatusParams>();

  const query = useQuery({
    queryKey: ["get ProgramStatusList", params],
    queryFn: async (): Promise<ProgramStatusList[]> => {
      if (!params) {
        return [];
      }
      const res = await apiInstance.get(
        API_ENDPOINTS.getProgramStatusListById(params.programId ?? ""),
      );
      return res.data;
    },
    enabled: !!params, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (newParams: StatusParams) => {
    setParams((prevParams) => ({
      ...newParams,
      isRefetch: !prevParams?.isRefetch, // Flip the boolean value
    }));
  };

  return { ...query, refetchWithParams };
};

export const useUpdateProgramMutation = () => {
  return useMutation({
    mutationFn: async (params: UpdateProgramParams) => {
      const response = await apiInstance.put(
        API_ENDPOINTS.updateProgramDetailById(params.id),
        params,
      );
      return response.data;
    },
  });
};

export const useSaveParticipantTabDetailsMutation = () => {
  return useMutation({
    mutationFn: async ({
      params,
      programId,
    }: {
      params: SaveParticipantParams;
      programId: string;
    }) => {
      const response = apiInstance.post(
        API_ENDPOINTS.saveParticipantInformationById(programId ?? ""),
        params,
      );
      return response;
    },
    retry: false,
  });
};

export const useGetParticipantDetailsById = () => {
  const [programId, setProgramId] = useState<string>();

  const query = useQuery({
    queryKey: ["useGetParticipantDetailsById", programId],
    queryFn: async (): Promise<ParticipantDetail> => {
      if (!programId) {
        return {} as ParticipantDetail;
      }
      const res = await apiInstance.get(
        API_ENDPOINTS.getParticipantInfoByProgramId(programId ?? ""),
      );
      return res.data;
    },
    enabled: !!programId, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (id: string) => {
    setProgramId(id);
    query.refetch();
  };

  return { ...query, refetchWithParams };
};

export const useUpdateParticipantTabDetailsMutation = () => {
  return useMutation({
    mutationFn: async ({
      params,
      programId,
    }: {
      params: SaveParticipantParams;
      programId: string;
    }) => {
      const response = apiInstance.put(
        API_ENDPOINTS.updateParticipantInformationById(programId ?? ""),
        params,
      );
      return response;
    },
    retry: false,
  });
};

export const useBestMatchPreferences = () => {
  return useQuery({
    queryKey: ["useBestMatchPreferences"],
    queryFn: async () => {
      const res = await apiInstance.get(
        API_ENDPOINTS.GET_BEST_MATCH_PREFERENCES,
      );
      return res.data;
    },
    retry: false,
  });
};

export const useSpecialAssignments = () => {
  return useQuery({
    queryKey: ["useSpecialAssignments"],
    queryFn: async () => {
      const res = await apiInstance.get(API_ENDPOINTS.GET_SPECIAL_ASSIGNMENTS);
      return res.data;
    },
    retry: false,
  });
};

export const useGetCurrencies = () => {
  return useQuery({
    queryKey: ["useGetCurrencies"],
    queryFn: async (): Promise<IdNameList[]> => {
      const res = await apiInstance.get(API_ENDPOINTS.GET_CURRENCIES);
      return res.data;
    },
    retry: false,
  });
};
export const useGetCurrenciesByCountryId = () => {
  const [countryId, setCountryId] = useState<string>();

  const query = useQuery({
    queryKey: ["getCurrenciesByCountryId", countryId],
    queryFn: async (): Promise<IdNameList[]> => {
      if (!countryId) {
        return [];
      }
      const res = await apiInstance.get(
        API_ENDPOINTS.GET_CURRENCIES_BY_COUNTRY(countryId ?? ""),
      );
      return res.data;
    },
    enabled: !!countryId, // Only run when params are set
    retry: false,
  });
  const refetchWithParams = (id: string) => {
    setCountryId(id);
    query.refetch();
  };

  return { ...query, refetchWithParams };
};

export const useGetTechnicalskills = () => {
  return useQuery({
    queryKey: ["useGetTechnicalskills"],
    queryFn: async (): Promise<IdNameList[]> => {
      const res = await apiInstance.get(API_ENDPOINTS.GET_TECHNICALSKILLS);
      return res.data;
    },
    retry: false,
  });
};
export const useGetSkills = () => {
  return useQuery({
    queryKey: ["useGetSkills"],
    queryFn: async (): Promise<IdNameList[]> => {
      const res = await apiInstance.get(API_ENDPOINTS.GET_SKILLS);
      return res.data;
    },
    retry: false,
  });
};

export const useGetTargetIndustries = () => {
  return useQuery({
    queryKey: ["useGetTargetIndustries"],
    queryFn: async (): Promise<IdNameList[]> => {
      const res = await apiInstance.get(API_ENDPOINTS.GET_TARGET_INDUSTRIES);
      return res.data;
    },
    retry: false,
  });
};

export const useSaveProgramStatusMutation = () => {
  return useMutation({
    mutationFn: async (params: SaveProgramParams) => {
      const response = await apiInstance.post(
        API_ENDPOINTS.saveProgramStatus(params.programId),
        params,
      );
      return response.data;
    },
  });
};

export const useUpdateProgramStatusMutation = () => {
  return useMutation({
    mutationFn: async (params: UpdateProgramStatusParams) => {
      const response = await apiInstance.put(
        API_ENDPOINTS.updateProgramStatus(params.programId, params.statusId),
        params,
      );
      return response.data;
    },
  });
};

export const useGetActivityTypesListByProgramId = () => {
  const [programId, setProgramId] = useState<string>();

  const query = useQuery({
    queryKey: ["useActivityTypesListByProgramId", programId],
    queryFn: async () => {
      if (!programId) {
        return [];
      }
      const res = await apiInstance.get(
        API_ENDPOINTS.getActivityTypeListByProgramId(programId ?? ""),
      );
      return res.data;
    },
    enabled: !!programId, // Only run when params are set
    retry: false,
  });
  const refetchWithParams = (id: string) => {
    setProgramId(id);
    query.refetch();
  };

  return { ...query, refetchWithParams };
};

export const useGetModalityById = () => {
  const [programId, setProgramId] = useState<string>();
  const [activityTypeKey, setActivityTypeKey] = useState<string>("");

  const query = useQuery({
    queryKey: ["useGetModalityById", programId, activityTypeKey],
    queryFn: async () => {
      if (!programId) {
        return [];
      }

      const res = await apiInstance.get(
        API_ENDPOINTS.getModalityListById(
          programId ?? "",
          activityTypeKey ?? "",
        ),
      );
      return res.data;
    },
    enabled: !!programId && !!activityTypeKey, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (id: string, activityTypeKey: string) => {
    setActivityTypeKey(activityTypeKey);
    setProgramId(id);
    query.refetch();
  };

  const clearListOfModalityData = () => {
    setProgramId("");
    setActivityTypeKey("");
  };

  return { ...query, refetchWithParams, clearListOfModalityData };
};

export const useGetTopicListByProgramId = () => {
  const [programId, setProgramId] = useState<string>();
  const [activityType, setActivityType] = useState<string>("");
  const [modalityKey, setModalityKey] = useState<string>("");

  const query = useQuery({
    queryKey: [
      "useGetTopicListByProgramId",
      programId,
      activityType,
      modalityKey,
    ],
    queryFn: async () => {
      if (!programId || !activityType || !modalityKey) {
        return [];
      }

      const queryParams = new URLSearchParams();
      queryParams.append("activityType", activityType);
      queryParams.append("modalityKey", modalityKey);

      let url = `${API_ENDPOINTS.getTopicList(programId ?? "")}?${queryParams.toString()}`;
      const res = await apiInstance.get(url);
      return res.data;
    },
    enabled: !!programId && !!activityType && !!modalityKey, // Ensure query runs only when all params are set
    retry: false,
  });

  const refetchWithParams = (
    id: string,
    newActivityType: string,
    newModalityKey: string,
  ) => {
    setProgramId(id);
    setActivityType(newActivityType);
    setModalityKey(newModalityKey);
    query.refetch();
  };

  const clearListOfTopicData = () => {
    setProgramId("");
    setActivityType("");
    setModalityKey("");
    // it will return [] array
  };

  return { ...query, refetchWithParams, clearListOfTopicData };
};

export const useSaveParticipantActivityForProgramMutation = () => {
  return useMutation({
    mutationFn: async ({
      params,
      programId,
    }: {
      params: ParticipantActivityParams;
      programId: string;
    }) => {
      const response = apiInstance.post(
        API_ENDPOINTS.saveActivityForProgram(programId),
        params,
      );
      return response;
    },
    retry: false,
  });
};

export const useGetParticipantActivitiesList = () => {
  const [params, setParams] = useState<ParticipantActivityTimelineParams>();

  const query = useQuery({
    queryKey: [
      "useGetParticipantActivitiesList",
      params?.id,
      params?.isFromGenericParticipant,
      params?.isFromNISParticipant,
    ],
    queryFn: async (): Promise<ParticipantActivityResponse[]> => {
      if (!params) {
        return [] as ParticipantActivityResponse[];
      }
      let url = API_ENDPOINTS.getActivitiesByProgramId(params?.id ?? "");
      if (params?.isFromGenericParticipant) {
        url = API_ENDPOINTS.getActivitiesByCandidateIdForGenericAccount(
          params?.id ?? "",
        );
      }
      if (params?.isFromNISParticipant) {
        url = API_ENDPOINTS.getNISParticipantActivityTimelineById(
          params?.id ?? "",
        );
      }
      const res = await apiInstance.get(url);
      return res.data;
    },
    enabled: !!params?.id, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (params: ParticipantActivityTimelineParams) => {
    setParams(params);
    query.refetch();
  };

  return { ...query, refetchWithParams };
};

export const useGetSessionsAndEventsList = (
  isFromGenericParticipant: boolean = false,
) => {
  const [programId, setProgramId] = useState<string>();

  const query = useQuery({
    queryKey: ["useGetSessionAndEventsList", programId],
    queryFn: async (): Promise<SessionsAndEventsResponse[]> => {
      if (!programId) {
        return [] as unknown as SessionsAndEventsResponse[];
      }
      let url = API_ENDPOINTS.getSessionAndEventsByProgramId(programId ?? "");
      if (isFromGenericParticipant) {
        url = API_ENDPOINTS.getSessionAndEventsByCandidateIdForGenericAccount(
          programId ?? "",
        );
      }
      const res = await apiInstance.get(url);
      return res.data as SessionsAndEventsResponse[];
    },
    enabled: !!programId, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (id: string) => {
    setProgramId(id);
    query.refetch();
  };

  return { ...query, refetchWithParams };
};

export const useUpdateParticipantActivityForProgramMutation = () => {
  return useMutation({
    mutationFn: async ({
      params,
      participantActivityTimeLineId,
    }: {
      params: ParticipantActivityParams;
      participantActivityTimeLineId: string;
    }) => {
      const response = apiInstance.put(
        API_ENDPOINTS.updateActivityForProgram(participantActivityTimeLineId),
        params,
      );
      return response;
    },
    retry: false,
  });
};
// coach Assignment API
export const useGetCoachAssignmentFunction = () => {
  return useQuery({
    queryKey: ["getCoachAssignmentFunction"],
    queryFn: async () => {
      const res = await apiInstance.get(
        API_ENDPOINTS.GET_PROGRAM_COACH_FUNCTION,
      );
      return res.data as CoachFunction[];
    },
    retry: false,
  });
};

export const useDeleteParticipantActivityTimeLineMutation = () => {
  return useMutation({
    mutationFn: (participantActivityTimeLineId: string) => {
      return apiInstance.delete(
        `${API_ENDPOINTS.deleteActivityFromTimeline(participantActivityTimeLineId)}`,
      );
    },
  });
};

export const useGetCoachesByFunctionId = () => {
  return useMutation({
    mutationFn: async (params: {
      identifier: string;
      deliveryCountry?: string;
    }) => {
      const queryParams = new URLSearchParams();
      queryParams.append("functionIdentifier", params.identifier);
      if (params?.deliveryCountry) {
        queryParams.append("deliveryCountry", params.deliveryCountry);
      }
      const url = `${API_ENDPOINTS.GET_COACH_STAFF_LIST}?${queryParams.toString()}`;
      const res = await apiInstance.get(url);
      return res.data as FunctionCoach[];
    },
    retry: false,
  });
};

export const useGetProgramPrimaryClosingReasonList = (programId: string) => {
  return useQuery({
    queryKey: ["getProgramPrimaryClosingReasonList"],
    queryFn: async () => {
      const res = await apiInstance.get(
        API_ENDPOINTS.getProgramPrimaryClosingReasonList(programId ?? ""),
      );
      return res.data;
    },
    retry: false,
  });
};

export const useGetSecondaryClosingReasonList = () => {
  const [params, setParams] = useState<SecondaryClosingParams>();

  const query = useQuery({
    queryKey: ["getSecondaryClosingReasonList", params],
    queryFn: async () => {
      if (!params) {
        return [];
      }
      const res = await apiInstance.get(
        API_ENDPOINTS.getProgramSecondaryClosingReasonList(
          params.programId,
          params.primaryClosingReasonId,
        ),
      );
      return res.data;
    },
    enabled: !!params, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (newParams: SecondaryClosingParams) => {
    setParams((prevParams) => ({
      ...newParams,
      isRefetch: !prevParams?.isRefetch, // Flip the boolean value
    }));
  };

  return { ...query, refetchWithParams };
};

export const useGetProgramCloserTimeToLand = () => {
  const [params, setParams] = useState<TimeToLandParams>();

  const query = useQuery({
    queryKey: ["getProgramCloserTimeToLand", params],
    queryFn: async () => {
      if (!params) {
        return [];
      }
      const res = await apiInstance.get(
        API_ENDPOINTS.getProgramClosingTimeToLandData(
          params.programId ?? "",
          params.landedDate ?? "",
        ),
      );
      return res.data;
    },
    enabled: !!params, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (newParams: TimeToLandParams) => {
    setParams((prevParams) => ({
      ...newParams,
      isRefetch: !prevParams?.isRefetch, // Flip the boolean value
    }));
  };

  return { ...query, refetchWithParams };
};
export const useTaxTypes = () => {
  return useQuery({
    queryKey: ["taxTypes"],
    queryFn: async (): Promise<TaxType[]> => {
      const response = await apiInstance.get(API_ENDPOINTS.TAX_TYPES);
      return (await response?.data) ?? [];
    },
  });
};
export const useGetProgramFeesAndExpenses = () => {
  return useMutation({
    mutationFn: async (programId: string): Promise<ProgramExpenseData[]> => {
      const res = await apiInstance.get(
        API_ENDPOINTS.getProgramFeesAndExpenseById(programId ?? ""),
      );
      return res.data;
    },
    retry: false,
  });
};

export const useGetProgramFeeDetails = () => {
  const [state, setState] = useState<{
    programId: string;
  }>();
  const query = useQuery({
    queryKey: ["useGetProgramFeeDetails", state],
    queryFn: async (): Promise<GetProgramFeeResponse> => {
      if (!state?.programId) {
        return {} as GetProgramFeeResponse;
      }
      const response = await apiInstance.get(
        API_ENDPOINTS.getProgramFeeDetailsByProgramId(state.programId ?? ""),
      );
      return await response?.data;
    },
    enabled: !!state?.programId,
    retry: false,
  });
  const refetchWithParams = (programId: string) => {
    setState({
      programId,
    });
  };
  return { ...query, refetchWithParams };
};

export const useGetClientBillingDetailsByClientId = () => {
  const [state, setState] = useState<GetBillingDetailsPayload>();

  const query = useQuery({
    queryKey: ["useGetClientBillingDetailsByClientId", state],
    queryFn: async (): Promise<GetBillingDetailsResponse> => {
      if (!state?.programTemplateId) {
        return {} as GetBillingDetailsResponse;
      }
      const res = await apiInstance.get(
        API_ENDPOINTS.getClientBillingDetails(
          getValueOrBlank(state.clientId),
          getValueOrBlank(state.programTemplateId),
          state.lineOfBusinessId,
        ),
      );
      return res.data;
    },
    enabled: !!state?.clientId, // Only run when params are set
    retry: false,
  });
  const refetchWithParams = (
    clientId: string,
    programTemplateId: string,
    lineOfBusinessId?: string,
  ) => {
    setState({
      clientId,
      programTemplateId,
      lineOfBusinessId,
    });
  };

  const resetParams = () => {
    setState({
      clientId: "",
      programTemplateId: "",
      lineOfBusinessId: "",
    });
  };
  return { ...query, refetchWithParams, resetParams };
};

export const useSaveProgramFeeDetailsMutation = () => {
  return useMutation({
    mutationFn: async ({
      params,
      programId,
    }: {
      params: SaveProgramFeeParams;
      programId: string;
    }) => {
      const response = apiInstance.post(
        API_ENDPOINTS.saveProgramFeeDetailsByProgramId(programId),
        params,
      );
      return response;
    },
    retry: false,
  });
};

export const useUpdateProgramFeeDetailsMutation = () => {
  return useMutation({
    mutationFn: async ({
      params,
      programId,
    }: {
      params: SaveProgramFeeParams;
      programId: string;
    }) => {
      const response = apiInstance.put(
        API_ENDPOINTS.updateProgramFeeDetailsByProgramId(programId ?? ""),
        params,
      );
      return response;
    },
    retry: false,
  });
};

export const useGetFeeType = () => {
  return useQuery({
    queryKey: ["useGetFeeType"],
    queryFn: async (): Promise<IdNameList[]> => {
      const response = await apiInstance.get(API_ENDPOINTS.GET_FEE_TYPE());
      return response?.data as IdNameList[];
    },
    retry: false,
  });
};

export const useGetExpenseCategory = () => {
  return useQuery({
    queryKey: ["useGetExpenseCategory"],
    queryFn: async (): Promise<IdNameList[]> => {
      const response = await apiInstance.get(
        API_ENDPOINTS.GET_EXPENSE_CATEGORY(),
      );
      return response?.data as IdNameList[];
    },
    retry: false,
  });
};

export const useGetExpenseTypeByCategory = () => {
  const [category, setCategory] = useState<string>();
  const query = useQuery({
    queryKey: ["useGetExpenseTypeByCategory", category],
    queryFn: async () => {
      if (!category) {
        return [];
      }
      const res = await apiInstance.get(
        API_ENDPOINTS.GET_EXPENSE_TYPE(category ?? ""),
      );
      return res.data as IdNameList[];
    },
    enabled: !!category, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (category: string) => {
    setCategory(category);
  };

  return { ...query, refetchWithParams };
};

export const useCreateProgramExpenseMutation = () => {
  return useMutation({
    mutationFn: async ({
      params,
      programId,
    }: {
      params: AddFeeOrExpenseRequestPayload;
      programId: string;
    }) => {
      const response = apiInstance.post(
        API_ENDPOINTS.createOrEditProgramFeeOrExpense(programId ?? ""),
        params,
      );
      return response;
    },
    retry: false,
  });
};

export const useEditProgramExpenseMutation = () => {
  return useMutation({
    mutationFn: async ({
      params,
      programId,
    }: {
      params: AddFeeOrExpenseRequestPayload;
      programId: string;
    }) => {
      const response = apiInstance.put(
        API_ENDPOINTS.createOrEditProgramFeeOrExpense(programId ?? ""),
        params,
      );
      return response;
    },
    retry: false,
  });
};

export const useGetFeesOrExpenseById = () => {
  const mutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiInstance.get(API_ENDPOINTS.getProgramExpense(id));
      return res.data as ViewFeeOrExpenseDetails;
    },
    retry: false,
  });

  return {
    ...mutation,
    fetchById: mutation.mutateAsync, // you call this with an ID
  };
};

export const useEditExpenseDetails = () => {
  return useMutation({
    mutationFn: async ({
      params,
      expenseId,
    }: {
      params: AddFeeOrExpenseRequestPayload;
      expenseId: string;
    }) => {
      const response = apiInstance.put(
        API_ENDPOINTS.getProgramExpense(expenseId),
        params,
      );
      return response;
    },
    retry: false,
  });
};
export const useGetPaymentTerms = () => {
  return useMutation({
    mutationFn: async (): Promise<IdNameList[]> => {
      const response = await apiInstance.get(API_ENDPOINTS.getPaymentTerms());
      return response.data as IdNameList[];
    },
    retry: false,
  });
};

export const useCompleteParticipantTransferMutation = () => {
  return useMutation({
    mutationFn: async ({ programId }: { programId: string }) => {
      const response = apiInstance.put(
        API_ENDPOINTS.completeParticipantTransfer(programId ?? ""),
      );
      return response;
    },
    retry: false,
  });
};

export const useGetManageParticipantsMetricsCountByProgramId = () => {
  const [state, setState] = useState<{
    programId: string;
  }>();
  const query = useQuery({
    queryKey: ["useGetManageParticipantsMetricsCountByProgramId", state],
    queryFn: async (): Promise<ManageParticipantsMetricsCountResponse> => {
      if (!state?.programId) {
        return {
          assessmentCount: 0,
          completedSessionsAndEventsCount: 0,
        };
      }
      const response = await apiInstance.get(
        API_ENDPOINTS.getManageParticipantsMetricsCount(state.programId ?? ""),
      );
      return response?.data as ManageParticipantsMetricsCountResponse;
    },
    enabled: !!state?.programId,
    retry: false,
  });
  const refetchWithParams = (programId: string) => {
    setState({
      programId,
    });
  };
  return { ...query, refetchWithParams };
};

export const useGetParticipantsList = () => {
  const [params, setParams] = useState<ParticipantsListPayload>();
  const query = useQuery({
    queryKey: ["useGetParticipantsList", params],
    queryFn: async (): Promise<ParticipantsListApiResponse[]> => {
      if (!params) {
        return [];
      }
      const { name = "" } = params;
      const res = await apiInstance.get(
        API_ENDPOINTS.getParticipantsList(name),
      );
      return res.data as ParticipantsListApiResponse[];
    },
    enabled: !!params, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (updatedParams: ParticipantsListPayload) => {
    setParams((previousParams) => ({
      ...updatedParams,
      isRefetch: !previousParams?.isRefetch,
    }));
  };

  return { ...query, refetchWithParams };
};

export const useCoachMetricsMutation = () => {
  return useMutation({
    mutationFn: async (
      params: CoachMetricsParams,
    ): Promise<CoachMetricsApiResponse> => {
      const response = await apiInstance.post(
        API_ENDPOINTS.GET_COACH_METRICS,
        params,
      );
      return response.data as CoachMetricsApiResponse;
    },
    retry: false,
  });
};

export const useGetCurrencyConversion = () => {
  const [params, setParams] = useState<currencyExchange>();
  const query = useQuery({
    queryKey: ["useGetCurrencyConversion", params],
    queryFn: async () => {
      if (!params) return null;

      const res = await apiInstance.get(API_ENDPOINTS.GET_CURRENCY_CONVERSION, {
        params: {
          transactionDate: params.transactionDate,
          expenseCurrencyId: params.expenseCurrency,
          billingCurrencyId: params.billingCurrency,
        },
        timeout: 5000, //Added Timeout Because it's required for this api and mention in ticket
      });

      return res.data;
    },
    enabled: !!params,
    retry: false,
  });

  const refetchWithParams = (
    transactionDate: string,
    expenseCurrency: string,
    billingCurrency: string,
  ) => {
    if (transactionDate && expenseCurrency && billingCurrency) {
      setParams({ transactionDate, expenseCurrency, billingCurrency });
    }
  };

  return { ...query, refetchWithParams };
};

export const useProgramActivateMutation = () => {
  return useMutation({
    mutationFn: async (params: {
      programId: string;
      nisParticipantId?: string;
    }) => {
      const response = await apiInstance.put(
        API_ENDPOINTS.activateProgram(
          params.programId,
          params?.nisParticipantId,
        ),
      );
      return response.data;
    },
  });
};

export const useUpdateNextContactDate = () => {
  return useMutation({
    mutationFn: async ({
      programId,
      nextContactDate,
    }: {
      programId: string;
      nextContactDate: string;
    }) => {
      const response = await apiInstance.put(
        API_ENDPOINTS.updateNextContactDate(programId),
        { programId, nextContactDate },
      );
      return response.data;
    },
  });
};

export const useGetProgramClosureData = (
  programId: string,
  programStatusId: string,
) => {
  return useQuery({
    queryKey: ["useGetProgramClosureData", programId],
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      queryParams.append("programStatusId", programStatusId);

      let url = `${API_ENDPOINTS.getProgramClosureData(programId)}?${queryParams.toString()}`;
      const res = await apiInstance.get(url);
      return res.data;
    },
    retry: false,
  });
};

export const useGetRevenueSplitForProgramInvoiceByInvoiceId = () => {
  return useMutation({
    mutationFn: async (params: GetRevenueSplitPayload) => {
      const baseUrl = params.isForProject
        ? API_ENDPOINTS.getRevenueSplitForProjectByInvoiceId(params.invoiceId)
        : API_ENDPOINTS.getRevenueSplitForProgramByInvoiceId(params.invoiceId);
      const res = await apiInstance.get(baseUrl);
      return res.data as RevenueSplitResponse;
    },
  });
};

export const useGetMigratedRevenueSplitForProjectByInvoiceId = () => {
  return useMutation({
    mutationFn: async (invoiceId: string) => {
      const baseUrl =
        API_ENDPOINTS.getMigratedRevenueSplitForProjectByInvoiceId(invoiceId);
      const res = await apiInstance.get(baseUrl);
      return res.data as MigratedRevenueSplitResponse;
    },
  });
};

export const useUpdateRevenueSplitForProgramInvoiceByInvoiceId = () => {
  return useMutation({
    mutationFn: async (params: UpdateRevenueSplitPayload) => {
      const baseUrl = params.isForProject
        ? API_ENDPOINTS.updateRevenueSplitForProjectByInvoiceId(
            params.invoiceId,
          )
        : API_ENDPOINTS.updateRevenueSplitForProgramByInvoiceId(
            params.invoiceId,
          );
      const res = await apiInstance.put(baseUrl, {
        invoiceId: params.invoiceId,
        officeId: params.officeId,
        counselingAmount: params.counselingAmount,
      });
      return res.data;
    },
  });
};

export const useGetPaymentTermByProgramId = () => {
  return useMutation({
    mutationFn: async (programId: string): Promise<IdNameList> => {
      const response = await apiInstance.get(
        API_ENDPOINTS.getPaymentTermByProgramId(programId),
      );
      return response.data as IdNameList;
    },
    retry: false,
  });
};

// coach Assignment API Individual
export const useGetTMIndividualCoachAssignmentFunction = () => {
  return useQuery({
    queryKey: ["getTMIndividualCoachAssignmentFunction"],
    queryFn: async () => {
      const res = await apiInstance.get(
        API_ENDPOINTS.GET_PROGRAM_TM_INDIVIDUAL_COACH_FUNCTION,
      );
      return res.data as CoachFunction[];
    },
    retry: false,
  });
};
// coach Assignment API Leader Coach
export const useGetTMLeaderCoachAssignmentFunction = () => {
  return useQuery({
    queryKey: ["getTMLeaderCoachAssignmentFunction"],
    queryFn: async () => {
      const res = await apiInstance.get(
        API_ENDPOINTS.GET_PROGRAM_TM_LEADER_COACH_FUNCTION,
      );
      return res.data as CoachFunction[];
    },
    retry: false,
  });
};

export const useGetGoalsListByProgramId = () => {
  return useMutation({
    mutationFn: async (programId: string): Promise<GoalSelectionResponse[]> => {
      const response = await apiInstance.get(
        API_ENDPOINTS.getGoalsListByProgramId(programId),
      );
      return response.data as GoalSelectionResponse[];
    },
    retry: false,
  });
};

export const useDeleteGoalByGoalId = () => {
  return useMutation({
    mutationFn: async (goalId: string) => {
      const response = await apiInstance.delete(
        API_ENDPOINTS.deleteGoalById(goalId),
      );
      return response.data;
    },
    retry: false,
  });
};

export const useAddGoalByProgramId = () => {
  return useMutation({
    mutationFn: async (params: AddGoalParams) => {
      const response = await apiInstance.post(
        API_ENDPOINTS.addGoalByProgramId(params.programId),
        params,
      );
      return response.data;
    },
    retry: false,
  });
};

export const useUpdateGoalById = () => {
  return useMutation({
    mutationFn: async (params: UpdateGoalParams) => {
      const response = await apiInstance.put(
        API_ENDPOINTS.updateGoalById(params.goalId),
        params,
      );
      return response.data;
    },
    retry: false,
  });
};

export const useGetGoalDetailsById = () => {
  const [params, setParams] = useState<string>();

  const query = useQuery({
    queryKey: ["getGoalDetailsById", params],
    queryFn: async (): Promise<GoalDetailResponse> => {
      if (!params) {
        return {} as GoalDetailResponse;
      }
      const res = await apiInstance.get(
        API_ENDPOINTS.getGoalDetailsById(params),
      );
      return res.data;
    },
    enabled: !!params, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (newParams: string) => {
    setParams(newParams);
    query.refetch();
  };

  return { ...query, refetchWithParams };
};

export const useProgramUploadDocumentDetailsMutation = () => {
  return useMutation({
    mutationFn: (params: ProgramDocumentUploadParams) => {
      return apiInstance.post(
        `${API_ENDPOINTS.programUploadDocumentsById(params.programId)}`,
        params,
      );
    },
  });
};
export const useGetProgramDocumentsList = () => {
  return useMutation({
    mutationFn: async (
      programId: string,
    ): Promise<ProgramDocumentDetailsResponse[]> => {
      const response = await apiInstance.get(
        API_ENDPOINTS.getProgramDocumentsList(programId),
      );
      return response.data as ProgramDocumentDetailsResponse[];
    },
    retry: false,
  });
};

export const useGetProgramDocumentDetailsById = () => {
  return useMutation({
    mutationFn: async (
      programDocumentId: string,
    ): Promise<ProgramDocumentDetailsResponse> => {
      const response = await apiInstance.get(
        API_ENDPOINTS.getProgramDocumentDetailsById(programDocumentId),
      );
      return response.data as ProgramDocumentDetailsResponse;
    },
    retry: false,
  });
};

export const useDeleteProgramDocumentMutation = () => {
  return useMutation({
    mutationFn: (programDocumentId: string) => {
      return apiInstance.delete(
        `${API_ENDPOINTS.deleteProgramDocument(programDocumentId)}`,
      );
    },
  });
};

export const useSearchCoachParticipantMutation = () => {
  return useMutation({
    mutationFn: (params: SearchCoachPortalParticipantsParams) => {
      return apiInstance.post(API_ENDPOINTS.COACH_PORTAL_SEARCH, params);
    },
    retry: false,
  });
};
